/**
 * Light-weight formatting helpers used across the dashboard pages.
 * These intentionally avoid fancy localisation libraries; they wrap
 * Intl.NumberFormat / Intl.DateTimeFormat with safe fallbacks.
 */

const SYMBOL_BY_CURRENCY: Record<string, string> = {
  NGN: '₦',
  USD: '$',
  GBP: '£',
  EUR: '€',
  KES: 'KSh',
  GHS: '₵',
  ZAR: 'R',
  CAD: 'C$',
  AUD: 'A$',
};

export function currencySymbol(code?: string): string {
  if (!code) return '';
  return SYMBOL_BY_CURRENCY[code.toUpperCase()] ?? code.toUpperCase() + ' ';
}

export function formatCurrency(
  value: number | string | null | undefined,
  currency: string = 'NGN',
  options: Intl.NumberFormatOptions = {},
): string {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
      ...options,
    }).format(Number(n));
  } catch {
    return `${currencySymbol(currency)}${Number(n).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    })}`;
  }
}

export function formatNumber(
  value: number | string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(undefined, options).format(Number(n));
}

export function formatPercent(
  value: number | string | null | undefined,
  fractionDigits: number = 1,
): string {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${Number(n).toFixed(fractionDigits)}%`;
}

export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(Number(value));
  } catch {
    return Number(value).toLocaleString();
  }
}

export function formatDateTime(value?: string | number | Date | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export function safeNumber(value: any, fallback: number = 0): number {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? Number(n) : fallback;
}
